import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  CalendarCheck,
  CalendarX,
  CheckCircle2,
  Crown,
  Eye,
  EyeOff,
  Heart,
  Loader2,
  LogOut,
  MoreVertical,
  Pencil,
  Receipt,
  Send,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  UserCheck,
  UserRoundX,
  UserX,
  X,
  Copy,
} from "lucide-react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { ref, listAll, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebase";
import AdminLayout from "../layout/AdminLayout";
import { PROFILE_VERIFICATION_STATUSES } from "../../utils/verification";
import {
  formatMembershipDate,
  isPremiumActive,
  parseMembershipDate,
  formatAmountPaise,
} from "../../utils/membership";

const profileSectionsConfig = [

  {
    title: "Personal Information",
    fields: [
      ["Profile Created By", "profileType"],
      ["Full Name", "fullName"],
      ["Gender", "gender"],
      ["Marital Status", "maritalStatus"],
      ["Date of Birth", "dateOfBirth"],
      ["Time of Birth", "timeOfBirth"],
      ["Height", "height"],
      ["Religion", "religion"],
      ["Caste", "caste"],
      ["Gotram", "gotram"],
      ["Rashi", "rashi"],
      ["Nakshatram", "nakshatram"],
      ["Birth Place", "birthPlace"],
      ["Country", "country"],
      ["About Me", "aboutMe"],
    ],
  },
  {
    title: "Education & Profession",
    fields: [
      ["Education", "education"],
      ["Other Education", "otherEducation"],
      ["College Name", "collegeName"],
      ["College Type", "collegeType"],
      ["Profession", "profession"],
      ["Other Profession", "otherProfession"],
      ["Company Name", "companyName"],
      ["Working Place", "workingPlace"],
      ["Annual Income", "annualIncome"],
      ["Other Information", "otherInformation"],
    ],
  },
  {
    title: "Family Information",
    fields: [
      ["Father Name", "fatherName"],
      ["Father Profession", "fatherProfession"],
      ["Father Designation", "fatherDesignation"],
      ["Mother Name", "motherName"],
      ["Mother Surname", "motherSurname"],
      ["Mother Profession", "motherProfession"],
      ["Brothers", "brothers"],
      ["Married Brothers", "marriedBrothers"],
      ["Sisters", "sisters"],
      ["Married Sisters", "marriedSisters"],
      ["Family Financial Status", "familyFinancialStatus"],
    ],
  },
  {
    title: "Partner Preferences",
    fields: [
      ["Preferred Age Range", "preferredAgeRange"],
      ["Preferred Location", "preferredLocation"],
      ["Preferred Education", "preferredEducation"],
      ["Preferred Profession", "preferredProfession"],
      ["Caste Preference", "castePreference"],
      ["Preferred Religion", "preferredReligion"],
      ["Preferred Marital Status", "preferredMaritalStatus"],
      ["Preferred Residence Type", "preferredResidenceType"],
      ["Other Expectations", "otherExpectations"],
    ],
  },
  {
    title: "Address",
    fields: [
      ["Residence Type", "residenceType"],
      ["Address Country", "addressCountry"],
      ["State", "state"],
      ["District", "district"],
      ["City / Town", "cityTown"],
      ["Area", "area"],
      ["Pincode", "pincode"],
      ["Visa Status", "visaStatus"],
      ["Other Visa Status", "otherVisaStatus"],
      ["Native Place", "nativePlace"],
      ["Present Residence", "presentResidence"],
    ],
  },
  {
    title: "Contact & Media",
    fields: [
      ["Mobile Number", "mobileNumber"],
      ["Alternate Number", "alternateNumber"],
      ["Interests", "interests"],
      ["Image Count", "imageCount"],
    ],
  },
  {
    title: "Privacy Settings",
    fields: [
      ["Profile Visibility", "profileVisibility"],
      ["Hide Photos", "hidePhotos"],
    ],
  },
    {
    title: "Account",
    fields: [
      ["Document ID", "id"],
      ["UID", "uid"],
      ["Profile ID", "profileId"],
      ["Role", "role"],
      ["Status", "status"],
      ["Membership", "membership"],
      ["Profile Status", "profileStatus"],
      ["Profile Completion", "profileCompletion"],
      ["Current Step", "currentStep"],
      ["Banned", "banned"],
      ["Hidden By Admin", "adminHidden"],
      ["Fraud Flag", "fraudFlag"],
      ["Force Logout At", "forceLogoutAt"],
      ["Last Active At", "lastActiveAt"],
      ["Last Active Page", "lastActivePage"],
      ["Created At", "createdAt"],
      ["Updated At", "updatedAt"],
    ],
  },
];

const actionCatalog = {
  editProfile: { key: "edit-profile", title: "Edit Profile", icon: Pencil, tone: "neutral" },
  blockUser: {
    key: "block-user",
    title: "Block User",
    icon: UserX,
    tone: "danger",
    changes: { status: "blocked" },
    successMessage: "User blocked.",
  },
  unblockUser: {
    key: "unblock-user",
    title: "Unblock User",
    icon: UserCheck,
    tone: "success",
    changes: { status: "active" },
    successMessage: "User unblocked.",
  },
  suspendAccount: {
    key: "suspend-account",
    title: "Suspend Account",
    icon: ShieldAlert,
    tone: "warning",
    changes: { status: "suspended" },
    successMessage: "Account suspended.",
  },
  activateAccount: {
    key: "activate-account",
    title: "Activate Account",
    icon: ShieldCheck,
    tone: "success",
    changes: { status: "active" },
    successMessage: "Account activated.",
  },
  deleteUser: {
    key: "delete-user",
    title: "Delete User",
    icon: Trash2,
    tone: "danger",
    deleteProfile: true,
    successMessage: "User profile deleted.",
  },
  hideProfile: {
    key: "hide-profile",
    title: "Hide Profile",
    icon: EyeOff,
    tone: "warning",
    changes: { adminHidden: true },
    successMessage: "Profile hidden.",
  },
  unhideProfile: {
    key: "unhide-profile",
    title: "Unhide Profile",
    icon: Eye,
    tone: "success",
    changes: { adminHidden: false },
    successMessage: "Profile visible again.",
  },
  banUser: {
    key: "ban-user",
    title: "Ban User",
    icon: Ban,
    tone: "danger",
    changes: { banned: true, status: "blocked" },
    successMessage: "User banned.",
  },
  unbanUser: {
    key: "unban-user",
    title: "Unban User",
    icon: ShieldCheck,
    tone: "success",
    changes: { banned: false, status: "active" },
    successMessage: "User unbanned.",
  },
  forceLogout: {
    key: "force-logout",
    title: "Force Logout",
    icon: LogOut,
    tone: "warning",
    changes: { forceLogoutAt: "SERVER_TIMESTAMP" },
    successMessage: "Force logout requested.",
  },
  approveVerification: {
    key: "approve-verification",
    title: "Approve Verification",
    icon: CheckCircle2,
    tone: "success",
    changes: {
      profileStatus: PROFILE_VERIFICATION_STATUSES.VERIFIED,
      verificationRemarks: "",
      verifiedAt: "SERVER_TIMESTAMP",
      reviewedAt: "SERVER_TIMESTAMP",
    },
    successMessage: "Profile approved and marked as verified.",
  },
  rejectVerification: {
    key: "reject-verification",
    title: "Reject Verification",
    icon: ShieldAlert,
    tone: "danger",
    changes: {
      profileStatus: PROFILE_VERIFICATION_STATUSES.REJECTED,
      reviewedAt: "SERVER_TIMESTAMP",
    },
    successMessage: "Profile verification rejected.",
  },
  requestChanges: {
    key: "request-changes",
    title: "Request Changes",
    icon: AlertTriangle,
    tone: "warning",
    changes: {
      profileStatus: PROFILE_VERIFICATION_STATUSES.CHANGES_REQUESTED,
      reviewedAt: "SERVER_TIMESTAMP",
    },
    successMessage: "Changes requested from the user.",
  },
  unverifyProfile: {
    key: "unverify-profile",
    title: "Unverify Profile",
    icon: X,
    tone: "warning",
    changes: {
      profileStatus: PROFILE_VERIFICATION_STATUSES.PENDING,
      verificationRemarks: "",
      verifiedAt: null,
      verifiedBy: null,
      reviewedAt: "SERVER_TIMESTAMP",
    },
    successMessage: "Profile moved back to pending verification.",
  },
  upgrade: {
    key: "upgrade",
    title: "Upgrade To Premium",
    icon: Crown,
    tone: "premium",
    changes: {},
    needsPlanSelection: true,
    successMessage: "Membership upgraded to premium.",
  },
  downgrade: {
    key: "downgrade",
    title: "Downgrade To Free",
    icon: UserRoundX,
    tone: "neutral",
    changes: { membership: "free" },
    successMessage: "Membership downgraded to free.",
  },
  markMarriageSettled: {
    key: "mark-marriage-settled",
    title: "Mark Marriage Settled",
    icon: CheckCircle2,
    tone: "warning",
    changes: { maritalStatus: "Marriage Settled" },
    successMessage: "Profile marked as marriage settled.",
  },
  markMarriageNotSettled: {
    key: "mark-marriage-not-settled",
    title: "Mark Marriage Not Settled",
    icon: ShieldCheck,
    tone: "success",
    changes: { maritalStatus: "Never Married" },
    successMessage: "Profile marked as marriage not settled.",
  },
  addFraudFlag: {
    key: "add-fraud-flag",
    title: "Add Fraud Flag",
    icon: AlertTriangle,
    tone: "danger",
    changes: { fraudFlag: true },
    successMessage: "Fraud flag added.",
  },
  removeFraudFlag: {
    key: "remove-fraud-flag",
    title: "Remove Fraud Flag",
    icon: CheckCircle2,
    tone: "success",
    changes: { fraudFlag: false },
    successMessage: "Fraud flag removed.",
  },
  copyBiodata: {
    key: "copy-biodata",
    title: "Copy Biodata",
    icon: Copy,
    tone: "neutral",
  },
};

function formatValue(value, key) {
  if (key === "profileVisibility") {
    const visibility = String(value || "").toLowerCase();
    if (!visibility) return "-";
    if (visibility === "everyone") return "Everyone";
    if (visibility === "premium") return "Premium only";
    if (visibility === "hidden") return "Hidden";
    return String(value);
  }
  if (key === "hidePhotos") {
    if (value === undefined || value === null || value === "") return "-";
    return value ? "Enabled" : "Disabled";
  }
  if (key === "profileCompletion" && value !== undefined && value !== null && value !== "") {
    return `${value}%`;
  }
  if (value === undefined || value === null || value === "") return "-";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    if (typeof value.toDate === "function") return value.toDate().toLocaleString();
    return JSON.stringify(value);
  }
  return String(value);
}

function getImageUrls(profile) {
  if (!profile) return [];
  return [profile.imageUrls, profile.images, profile.photos]
    .flatMap((item) => (Array.isArray(item) ? item : item ? [item] : []))
    .map((item) => (typeof item === "string" ? item : item?.url || item?.downloadURL || item?.src || ""))
    .filter(Boolean);
}

function buildBiodataText(profile) {
  const imageUrls = getImageUrls(profile);
  const sections = buildSections(profile, imageUrls);
  const lines = [];

  sections.forEach((section) => {
    const visibleFields = section.fields
      .map((field) => ({ label: field.label, key: field.key, value: formatValue(field.value, field.key) }))
        .filter((field) => {
        if (section.title === "Contact & Media") {
          const allowedContactFields = ["mobileNumber", "alternateNumber"];
          if (!allowedContactFields.includes(field.key)) return false;
        }
        if (field.key === "imageCount" && field.value === "0") return false;
        return field.value !== "-" && field.value !== "" && field.value !== null && field.value !== undefined;
      });

    if (!visibleFields.length) return;
    if (section.title === "Account" || section.title === "Partner Preferences") return;

    lines.push(section.title.toUpperCase());
    visibleFields.forEach((field) => {
      lines.push(`${field.label}: ${field.value}`);
    });
    lines.push("");
  });

  return lines.join("\n").trim();
}

function getToneClasses(tone) {
  if (tone === "danger") return "text-red-700 hover:bg-red-50";
  if (tone === "warning") return "text-amber-700 hover:bg-amber-50";
  if (tone === "success") return "text-emerald-700 hover:bg-emerald-50";
  if (tone === "premium") return "text-[#c90243] hover:bg-[#fff3ec]";
  return "text-[#2F2723] hover:bg-[#FAF7F2]";
}

function buildSections(profile, imageUrls) {
  const hydrated = {
    ...profile,
    imageCount: imageUrls.length,
  };

  return profileSectionsConfig.map((section) => ({
    title: section.title,
    fields: section.fields
      .map(([label, key]) => ({
        label,
        key,
        value: hydrated[key],
      }))
      .filter((field) => {
        if (
          field.key === "otherProfession" &&
          hydrated.profession &&
          hydrated.otherProfession &&
          hydrated.profession === hydrated.otherProfession
        ) {
          return false;
        }
        if (
          field.key === "otherEducation" &&
          hydrated.education &&
          hydrated.otherEducation &&
          hydrated.education === hydrated.otherEducation
        ) {
          return false;
        }
        return true;
      }),
  }));
}

function getActionGroups(profile) {
  const currentPlan = profile?.membership || "free";
  const currentStatus = profile?.status || "active";
  const maritalStatus = String(profile?.maritalStatus || "").toLowerCase();
  const isMarriageSettled =
    maritalStatus === "marriage settled" ||
    maritalStatus === "married" ||
    maritalStatus === "settled";

  // Verification actions
  const verificationActions =
    profile?.profileStatus === PROFILE_VERIFICATION_STATUSES.PENDING
      ? [
          actionCatalog.approveVerification,
          actionCatalog.requestChanges,
          actionCatalog.rejectVerification,
        ]
      : profile?.profileStatus === PROFILE_VERIFICATION_STATUSES.VERIFIED
        ? [actionCatalog.unverifyProfile]
        : [];

  // Plan actions
  const planActions =
    currentPlan === "premium" ? [actionCatalog.downgrade] : [actionCatalog.upgrade];

  // Account actions (simplified)
  const accountActions = [
    actionCatalog.editProfile,
    currentStatus === "blocked" ? actionCatalog.unblockUser : actionCatalog.blockUser,
    isMarriageSettled ? actionCatalog.markMarriageNotSettled : actionCatalog.markMarriageSettled,
    actionCatalog.deleteUser,
  ];

  return [
    ...(verificationActions.length
      ? [{ title: "Verification", actions: verificationActions }]
      : []),
    { title: "Membership", actions: planActions },
    { title: "Account", actions: accountActions },
    { title: "Utility", actions: [actionCatalog.copyBiodata] },
  ];
}

function DetailRow({ label, value }) {
  return (
    <div className="rounded-2xl bg-[#FAF7F2] px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8B766B]">{label}</p>
      <p className="mt-1 break-words text-sm text-[#2F2723]">{value || "-"}</p>
    </div>
  );
}

function ActionMenu({ groups, profile, savingKey, onAction }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-sand-200 bg-white text-ink-700 shadow-soft transition hover:border-brand-600 hover:text-brand-600"
        aria-label="Admin actions"
      >
        <MoreVertical size={18} />
      </button>

      {/* Slide-out panel */}
      {open && (
        <>
          <div className="fixed inset-0 z-[60] bg-ink-900/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="fixed bottom-0 right-0 top-0 z-[65] flex w-full max-w-sm flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-sand-200 px-5 py-4">
              <div>
                <h2 className="font-display text-lg font-bold text-ink-800">Admin Actions</h2>
                <p className="text-[12px] text-ink-400">Apply actions to this profile</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="btn-icon h-9 w-9">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {groups.map((group) => (
                <div key={group.title} className="mb-4">
                  <p className="mb-2 text-[10.5px] font-bold uppercase tracking-wider text-ink-400">{group.title}</p>
                  <div className="space-y-1.5">
                    {group.actions.map((action) => {
                      const Icon = action.icon;
                      const toneClass =
                        action.tone === "danger" ? "text-rose-600 hover:bg-rose-50" :
                        action.tone === "premium" ? "text-amber-700 hover:bg-amber-50" :
                        action.tone === "warning" ? "text-amber-700 hover:bg-amber-50" :
                        action.tone === "success" ? "text-emerald-700 hover:bg-emerald-50" :
                        "text-ink-700 hover:bg-sand-50";
                      return (
                        <button
                          key={action.key}
                          type="button"
                          disabled={Boolean(savingKey)}
                          onClick={() => { setOpen(false); onAction(action); }}
                          className={`flex w-full items-center gap-3 rounded-2xl px-3.5 py-3 text-left text-[13.5px] font-semibold transition disabled:opacity-40 ${toneClass}`}
                        >
                          <Icon size={17} />
                          {action.title}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function ActionModal({ action, busy, onClose, onConfirm, selectedPlan, setSelectedPlan }) {
  if (!action) return null;
  const Icon = action.icon;

  // Min date = today, formatted as YYYY-MM-DD for the date input
  const todayStr = new Date().toISOString().split("T")[0];

  const PLAN_OPTIONS = [
    { id: "premium_6months", name: "6 Months Premium", months: 6, price: "₹4,999" },
    { id: "premium_annual", name: "1 Year Premium", months: 12, price: "₹6,999" },
    { id: "premium_custom", name: "Custom End Date", months: null, price: "Set manually" },
  ];

  const isCustom = selectedPlan?.id === "premium_custom";

  // Format selected custom date nicely for the summary label
  const customDateLabel = selectedPlan?.customDate
    ? new Date(selectedPlan.customDate + "T00:00:00").toLocaleDateString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
      })
    : null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink-900/50 px-4 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-elevated" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 border-b border-sand-200 px-6 pt-6 pb-4">
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
            action.tone === "danger" ? "bg-rose-50 text-rose-600" :
            action.tone === "premium" ? "bg-amber-50 text-amber-600" :
            action.tone === "warning" ? "bg-amber-50 text-amber-600" :
            "bg-brand-50 text-brand-600"
          }`}>
            <Icon size={22} />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold text-ink-800">{action.title}</h3>
            <p className="mt-0.5 text-[13px] text-ink-500">Confirm this action for the user.</p>
          </div>
        </div>

        <div className="px-6 py-4">
          {action.needsPlanSelection ? (
            <div className="space-y-3">
              <p className="text-[13px] font-semibold text-ink-700">Select plan to activate:</p>
              {PLAN_OPTIONS.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setSelectedPlan(plan.id === "premium_custom"
                    ? { ...plan, customDate: selectedPlan?.customDate || "" }
                    : plan
                  )}
                  className={`flex w-full items-center justify-between rounded-2xl border-2 p-4 text-left transition ${
                    selectedPlan?.id === plan.id
                      ? "border-brand-600 bg-brand-50"
                      : "border-sand-200 bg-white hover:border-brand-300"
                  }`}
                >
                  <div>
                    <p className="text-[14px] font-bold text-ink-800">{plan.name}</p>
                    <p className="text-[12px] text-ink-400">
                      {plan.months
                        ? `${plan.months} months validity • ${plan.price}`
                        : "Admin picks exact expiry date"}
                    </p>
                  </div>
                  {selectedPlan?.id === plan.id && (
                    <CheckCircle2 size={20} className="text-brand-600" />
                  )}
                </button>
              ))}

              {/* Custom date picker — only shown when Custom End Date is selected */}
              {isCustom && (
                <div className="rounded-2xl border-2 border-brand-600 bg-brand-50 p-4">
                  <label className="mb-1.5 block text-[12px] font-bold uppercase tracking-wide text-ink-500">
                    Premium valid until
                  </label>
                  <input
                    type="date"
                    min={todayStr}
                    value={selectedPlan.customDate || ""}
                    onChange={(e) =>
                      setSelectedPlan((prev) => ({ ...prev, customDate: e.target.value }))
                    }
                    className="w-full rounded-xl border border-sand-200 bg-white px-4 py-2.5 text-[14px] font-semibold text-ink-800 outline-none focus:border-brand-600"
                  />
                  {customDateLabel && (
                    <p className="mt-1.5 text-[12px] text-ink-500">
                      Premium expires on <span className="font-bold text-ink-700">{customDateLabel}</span>. After this date the user will automatically return to free.
                    </p>
                  )}
                </div>
              )}

              <p className="text-[11.5px] text-ink-400">
                {isCustom
                  ? "Use this for custom deals, extended trials, or special arrangements."
                  : `Premium starts today and expires after ${selectedPlan?.months || "—"} months. Use this for manual/offline payments.`}
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-sand-200 bg-sand-50 px-4 py-3 text-[13.5px] text-ink-600">
              Confirm <span className="font-bold text-ink-800">{action.title}</span> for this user.
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 border-t border-sand-200 px-6 py-4">
          <button type="button" onClick={onClose} disabled={busy} className="btn-ghost py-3">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={
              busy ||
              (action.needsPlanSelection && !selectedPlan) ||
              (action.needsPlanSelection && isCustom && !selectedPlan?.customDate)
            }
            className="btn-primary py-3 disabled:opacity-50"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function MembershipCard({ profile }) {
  const isActive = isPremiumActive(profile);
  const membership = String(profile?.membership || "free").toLowerCase();
  const isPremium = membership === "premium";
  const expiresAt = parseMembershipDate(profile?.membershipExpiresAt);
  const startedAt = parseMembershipDate(profile?.membershipStartedAt);
  const isExpired = isPremium && expiresAt && expiresAt.getTime() <= Date.now();
  const isAdminGrant = profile?.membershipSource === "admin";

  const statusLabel = isActive ? "Active" : isExpired ? "Expired" : "Free";
  const statusColor = isActive
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : isExpired
    ? "bg-rose-50 text-rose-700 border-rose-200"
    : "bg-sand-100 text-ink-500 border-sand-200";

  const cardBorder = isActive
    ? "border-amber-200 bg-gradient-to-br from-amber-50 to-white"
    : isExpired
    ? "border-rose-200 bg-rose-50/30"
    : "border-sand-200 bg-white";

  return (
    <section className={`overflow-hidden rounded-[24px] border ${cardBorder} p-5`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${
            isActive ? "bg-amber-100 text-amber-600" : "bg-sand-100 text-ink-400"
          }`}>
            <Crown size={20} />
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-ink-400">Membership</p>
            <h3 className="font-display text-lg font-bold text-ink-800">
              {isPremium
                ? profile?.membershipPlanName || "Premium"
                : "Free Plan"}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdminGrant && isPremium && (
            <span className="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-[11px] font-bold text-brand-700">
              Admin Assigned
            </span>
          )}
          <span className={`rounded-full border px-3 py-1 text-[11px] font-bold ${statusColor}`}>
            {statusLabel}
          </span>
        </div>
      </div>

      {/* Details grid */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {/* Plan ID */}
        <div className="rounded-2xl bg-white border border-sand-200 px-3.5 py-3">
          <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-400">Plan ID</p>
          <p className="mt-1 break-all text-[12.5px] font-semibold text-ink-800">
            {profile?.membershipPlanId || "-"}
          </p>
        </div>

        {/* Duration */}
        <div className="rounded-2xl bg-white border border-sand-200 px-3.5 py-3">
          <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-400">Duration</p>
          <p className="mt-1 text-[12.5px] font-semibold text-ink-800">
            {profile?.membershipDurationMonths
              ? `${profile.membershipDurationMonths} months`
              : isPremium ? "Premium" : "-"}
          </p>
        </div>

        {/* Amount paid — only show for Razorpay payments, not admin grants */}
        <div className="rounded-2xl bg-white border border-sand-200 px-3.5 py-3">
          <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-400">Amount Paid</p>
          <p className="mt-1 text-[12.5px] font-semibold text-[#c90243]">
            {!isAdminGrant && profile?.lastPaymentAmount
              ? formatAmountPaise(profile.lastPaymentAmount, profile.lastPaymentCurrency || "INR")
              : isAdminGrant && isPremium
              ? <span className="text-brand-600">Admin Assigned</span>
              : "-"}
          </p>
        </div>

        {/* Started on */}
        <div className="rounded-2xl bg-white border border-sand-200 px-3.5 py-3">
          <div className="flex items-center gap-1.5">
            <CalendarCheck size={12} className="text-emerald-500" />
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-400">Started On</p>
          </div>
          <p className="mt-1 text-[12.5px] font-semibold text-ink-800">
            {startedAt ? formatMembershipDate(startedAt) : "-"}
          </p>
        </div>

        {/* Expires on */}
        <div className="rounded-2xl bg-white border border-sand-200 px-3.5 py-3">
          <div className="flex items-center gap-1.5">
            <CalendarX size={12} className={isExpired ? "text-rose-500" : "text-ink-400"} />
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-400">
              {isExpired ? "Expired On" : "Expires On"}
            </p>
          </div>
          <p className={`mt-1 text-[12.5px] font-semibold ${isExpired ? "text-rose-600" : "text-ink-800"}`}>
            {expiresAt ? formatMembershipDate(expiresAt) : "-"}
          </p>
        </div>

        {/* Payment ID — only show for Razorpay payments, not admin grants */}
        <div className="rounded-2xl bg-white border border-sand-200 px-3.5 py-3">
          <div className="flex items-center gap-1.5">
            <Receipt size={12} className="text-ink-400" />
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-400">Payment ID</p>
          </div>
          <p className="mt-1 break-all text-[11px] font-semibold text-ink-600">
            {!isAdminGrant && profile?.lastPaymentId
              ? profile.lastPaymentId
              : isAdminGrant && isPremium
              ? <span className="text-brand-600">Admin Assigned</span>
              : "-"}
          </p>
        </div>
      </div>

      {/* Downgraded note */}
      {profile?.membershipDowngradedAt && !isPremium && (
        <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-3.5 py-2.5">
          <p className="text-[12px] font-semibold text-amber-800">
            Auto-downgraded to free on{" "}
            {formatMembershipDate(parseMembershipDate(profile.membershipDowngradedAt))}.
          </p>
        </div>
      )}
    </section>
  );
}

function formatInterestDate(ts) {
  if (!ts) return null;
  const d =
    typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function InterestStatusBadge({ status }) {
  const s = String(status || "pending").toLowerCase();
  const cfg = {
    accepted: {
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dot: "bg-emerald-500",
      label: "Accepted",
    },
    rejected: {
      cls: "bg-rose-50 text-rose-700 border-rose-200",
      dot: "bg-rose-500",
      label: "Rejected",
    },
    declined: {
      cls: "bg-rose-50 text-rose-700 border-rose-200",
      dot: "bg-rose-500",
      label: "Declined",
    },
  }[s] || {
    cls: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-400",
    label: "Pending",
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${cfg.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function InterestCard({ item, direction, onViewProfile }) {
  const sentDate = formatInterestDate(item.createdAt);
  const respondedDate = formatInterestDate(item.updatedAt);
  const status = String(item.status || "pending").toLowerCase();
  const hasResponse = status === "accepted" || status === "rejected" || status === "declined";

  const initials = item.counterpartName
    ? item.counterpartName
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "?";

  const genderColor =
    String(item.counterpartGender || "").toLowerCase() === "female"
      ? "bg-pink-50 text-pink-600"
      : String(item.counterpartGender || "").toLowerCase() === "male"
      ? "bg-blue-50 text-blue-600"
      : "bg-[#FAF7F2] text-ink-500";

  return (
    <div className="group rounded-2xl border border-[#EFE4DD] bg-white transition hover:border-[#c90243]/30 hover:shadow-sm">
      <div className="flex items-center gap-3 px-4 pt-3 pb-2">
        {/* Photo / Avatar */}
        <div
          className={`relative h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-[#EFE4DD] ${
            direction === "sent" ? "ring-1 ring-[#c90243]/20" : "ring-1 ring-emerald-200"
          }`}
        >
          {item.counterpartPhoto ? (
            <img
              src={item.counterpartPhoto}
              alt={item.counterpartName}
              className="h-full w-full object-cover object-center"
            />
          ) : (
            <div
              className={`flex h-full w-full items-center justify-center text-[13px] font-bold ${
                direction === "sent"
                  ? "bg-[#fff0f4] text-[#c90243]"
                  : "bg-emerald-50 text-emerald-700"
              }`}
            >
              {initials}
            </div>
          )}
        </div>

        {/* Name + details */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-bold text-[#231815]">
            {item.counterpartName}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            {item.counterpartProfileId && (
              <span className="rounded-md bg-[#FAF7F2] px-1.5 py-0.5 text-[10px] font-bold text-[#c90243]">
                {item.counterpartProfileId}
              </span>
            )}
            {item.counterpartGender && (
              <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${genderColor}`}>
                {item.counterpartGender}
              </span>
            )}
            {item.counterpartEducation && (
              <span className="rounded-md bg-[#FAF7F2] px-1.5 py-0.5 text-[10px] font-medium text-ink-500 max-w-[120px] truncate">
                {item.counterpartEducation}
              </span>
            )}
          </div>
        </div>

        {/* Status badge + view */}
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <InterestStatusBadge status={item.status} />
          {item.counterpartId && (
            <button
              type="button"
              onClick={onViewProfile}
              className="text-[10.5px] font-semibold text-[#c90243] opacity-0 transition group-hover:opacity-100 hover:underline"
            >
              View profile →
            </button>
          )}
        </div>
      </div>

      {/* Date timeline strip */}
      <div className="mx-4 mb-3 mt-1 flex flex-wrap items-center gap-0 rounded-xl bg-[#FAF7F2] px-3 py-2 text-[10.5px]">
        {/* Sent date */}
        <div className="flex items-center gap-1.5">
          <Send size={10} className="shrink-0 text-[#c90243]" />
          <span className="font-semibold text-ink-400">Sent</span>
          <span className="font-bold text-ink-700">{sentDate || "—"}</span>
        </div>

        {hasResponse && respondedDate && respondedDate !== sentDate ? (
          <>
            <div className="mx-2 flex items-center gap-1 text-ink-300">
              <div className="h-px w-4 bg-[#E7E0DC]" />
              <span className="text-[8px]">▶</span>
            </div>
            <div className="flex items-center gap-1.5">
              {status === "accepted" ? (
                <CheckCircle2 size={10} className="shrink-0 text-emerald-500" />
              ) : (
                <X size={10} className="shrink-0 text-rose-500" />
              )}
              <span className="font-semibold text-ink-400">
                {status === "accepted" ? "Accepted" : "Declined"}
              </span>
              <span className="font-bold text-ink-700">{respondedDate}</span>
            </div>
          </>
        ) : hasResponse && (!respondedDate || respondedDate === sentDate) ? (
          <>
            <div className="mx-2 flex items-center gap-1 text-ink-300">
              <div className="h-px w-4 bg-[#E7E0DC]" />
              <span className="text-[8px]">▶</span>
            </div>
            <div className="flex items-center gap-1.5">
              {status === "accepted" ? (
                <CheckCircle2 size={10} className="shrink-0 text-emerald-500" />
              ) : (
                <X size={10} className="shrink-0 text-rose-500" />
              )}
              <span className="font-semibold text-ink-400">
                {status === "accepted" ? "Accepted" : "Declined"}
              </span>
              <span className="font-bold text-ink-700">same day</span>
            </div>
          </>
        ) : (
          <>
            <div className="mx-2 flex items-center gap-1 text-ink-300">
              <div className="h-px w-4 bg-[#E7E0DC]" />
              <span className="text-[8px]">▶</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-1.5 w-1.5 rounded-full bg-amber-400" />
              <span className="font-semibold text-amber-600">Awaiting response</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InterestPanel({ items, direction, onViewProfile }) {
  const [showAll, setShowAll] = useState(false);
  const INITIAL_LIMIT = 5;
  const visible = showAll ? items : items.slice(0, INITIAL_LIMIT);
  const hasMore = items.length > INITIAL_LIMIT;

  const statusCounts = items.reduce((acc, item) => {
    const s = String(item.status || "pending").toLowerCase();
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#E7E0DC] bg-[#FCFAF8] py-10 text-center">
        {direction === "sent" ? (
          <Send size={24} className="mb-2 text-[#c90243]/30" />
        ) : (
          <Heart size={24} className="mb-2 text-emerald-400/50" />
        )}
        <p className="text-[13px] font-semibold text-ink-400">
          No {direction} requests
        </p>
        <p className="mt-1 text-[11px] text-ink-300">
          {direction === "sent"
            ? "This user hasn't sent any interest yet"
            : "No one has sent interest to this user yet"}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Mini stats strip */}
      {items.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {Object.entries(statusCounts).map(([s, count]) => (
            <span
              key={s}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-bold ${
                s === "accepted"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : s === "rejected" || s === "declined"
                  ? "border-rose-200 bg-rose-50 text-rose-700"
                  : "border-amber-200 bg-amber-50 text-amber-700"
              }`}
            >
              {count} {s.charAt(0).toUpperCase() + s.slice(1)}
            </span>
          ))}
        </div>
      )}

      {/* Cards */}
      <div className="space-y-2">
        {visible.map((item) => (
          <InterestCard
            key={item.id}
            item={item}
            direction={direction}
            onViewProfile={() => onViewProfile(item.counterpartId)}
          />
        ))}
      </div>

      {/* Show more / less */}
      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-3 w-full rounded-2xl border border-dashed border-[#E7E0DC] py-2.5 text-[12px] font-semibold text-ink-400 transition hover:border-[#c90243]/40 hover:text-[#c90243]"
        >
          {showAll ? "Show less" : `Show ${items.length - INITIAL_LIMIT} more`}
        </button>
      )}
    </div>
  );
}

function InterestRequestsSection({ interests, interestsLoading, onViewProfile }) {
  const [activeTab, setActiveTab] = useState("sent");
  const total = interests.sent.length + interests.received.length;

  const tabs = [
    {
      key: "sent",
      label: "Sent",
      count: interests.sent.length,
      icon: Send,
      color: "text-[#c90243]",
      activeBg: "bg-[#fff0f4] text-[#c90243] border-[#f5c6d4]",
    },
    {
      key: "received",
      label: "Received",
      count: interests.received.length,
      icon: Heart,
      color: "text-emerald-600",
      activeBg: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
  ];

  return (
    <section className="rounded-[28px] border border-[#E7E0DC] bg-white overflow-hidden">
      {/* Header */}
      <div className="border-b border-[#F3EEEA] bg-[#FDFAF8] px-6 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#fff0f4]">
              <Heart size={18} className="text-[#c90243]" />
            </div>
            <div>
              <h3 className="text-[15px] font-bold text-[#231815]">Interest Requests</h3>
              <p className="text-[11.5px] text-ink-400">
                {total === 0 ? "No activity yet" : `${total} total request${total !== 1 ? "s" : ""}`}
              </p>
            </div>
          </div>

          {/* Summary pills */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-2xl border border-[#EFE4DD] bg-white px-3 py-1.5">
              <Send size={12} className="text-[#c90243]" />
              <span className="text-[12px] font-bold text-[#231815]">{interests.sent.length}</span>
              <span className="text-[11px] text-ink-400">sent</span>
            </div>
            <div className="flex items-center gap-1.5 rounded-2xl border border-[#EFE4DD] bg-white px-3 py-1.5">
              <Heart size={12} className="text-emerald-600" />
              <span className="text-[12px] font-bold text-[#231815]">{interests.received.length}</span>
              <span className="text-[11px] text-ink-400">received</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 rounded-xl border px-4 py-2 text-[12.5px] font-bold transition ${
                  isActive
                    ? tab.activeBg
                    : "border-transparent bg-transparent text-ink-400 hover:bg-[#FAF7F2] hover:text-ink-700"
                }`}
              >
                <Icon size={13} />
                {tab.label}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                    isActive ? "bg-white/60" : "bg-[#FAF7F2]"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        {interestsLoading ? (
          <div className="flex items-center gap-3 py-6 text-sm text-ink-400">
            <Loader2 size={16} className="animate-spin text-[#c90243]" />
            Loading interest requests…
          </div>
        ) : (
          <InterestPanel
            key={activeTab}
            items={activeTab === "sent" ? interests.sent : interests.received}
            direction={activeTab}
            onViewProfile={onViewProfile}
          />
        )}
      </div>
    </section>
  );
}

const AdminUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [openAccount, setOpenAccount] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [interests, setInterests] = useState({ sent: [], received: [] });
  const [interestsLoading, setInterestsLoading] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError("");

      try {
        const snapshot = await getDoc(doc(db, "profiles", userId));
        if (!snapshot.exists()) {
          setProfile(null);
          setError("User profile not found.");
          setLoading(false);
          return;
        }

        setProfile({ id: snapshot.id, ...snapshot.data() });
        setLoading(false);
      } catch (fetchError) {
        setError(fetchError.message || "Failed to load user profile.");
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  // Fetch interest requests (sent + received) for this user
  useEffect(() => {
    if (!userId) return;

    const fetchInterests = async () => {
      setInterestsLoading(true);
      try {
        const interestsRef = collection(db, "interests");

        // Requests this user SENT
        const sentSnap = await getDocs(
          query(interestsRef, where("fromUserId", "==", userId))
        );
        // Requests this user RECEIVED
        const receivedSnap = await getDocs(
          query(interestsRef, where("toUserId", "==", userId))
        );

        // Collect all counterpart profile IDs to batch-fetch names
        const sentItems = sentSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const receivedItems = receivedSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const counterpartIds = [
          ...new Set([
            ...sentItems.map((r) => r.toUserId),
            ...receivedItems.map((r) => r.fromUserId),
          ]),
        ].filter(Boolean);

        // Fetch all counterpart profiles in parallel
        const profileMap = {};
        await Promise.all(
          counterpartIds.map(async (pid) => {
            try {
              const snap = await getDoc(doc(db, "profiles", pid));
              if (snap.exists()) {
                profileMap[pid] = snap.data();
              }
            } catch {
              // ignore individual failures
            }
          })
        );

        const enrich = (item, counterpartKey) => {
          const p = profileMap[item[counterpartKey]] || {};
          // Get first photo from any image field
          const photos = [p.imageUrls, p.images, p.photos]
            .flatMap((v) => (Array.isArray(v) ? v : v ? [v] : []))
            .map((v) => (typeof v === "string" ? v : v?.url || v?.downloadURL || v?.src || ""))
            .filter(Boolean);

          return {
            ...item,
            counterpartId: item[counterpartKey],
            counterpartName: p.fullName || p.name || "Unknown",
            counterpartProfileId: p.kmProfileId || p.profileId || null,
            counterpartPhoto: photos[0] || null,
            counterpartEducation: p.education || null,
            counterpartGender: p.gender || null,
          };
        };

        setInterests({
          sent: sentItems.map((r) => enrich(r, "toUserId")),
          received: receivedItems.map((r) => enrich(r, "fromUserId")),
        });
      } catch (err) {
        console.error("Failed to load interests:", err);
      } finally {
        setInterestsLoading(false);
      }
    };

    fetchInterests();
  }, [userId]);

  const imageUrls = useMemo(() => getImageUrls(profile), [profile]);
  const sections = useMemo(() => (profile ? buildSections(profile, imageUrls) : []), [profile, imageUrls]);
  const actionGroups = useMemo(() => getActionGroups(profile), [profile]);
  const [copiedBiodata, setCopiedBiodata] = useState(false);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(null);
      else if (e.key === "ArrowRight") setLightboxIdx((i) => (i + 1) % imageUrls.length);
      else if (e.key === "ArrowLeft") setLightboxIdx((i) => (i - 1 + imageUrls.length) % imageUrls.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx, imageUrls.length]);

  const runAction = async (action) => {
    if (!profile || !action) return;
    setSavingKey(action.key);
    setError("");
    setNotice("");

    try {
      if (action.key === actionCatalog.editProfile.key) {
        navigate(`/admin/users/${profile.id}/edit`);
        setPendingAction(null);
        return;
      }

      if (action.deleteProfile) {
        // Delete photos from Storage
        try {
          const photosRef = ref(storage, `profile-images/${profile.id}`);
          const photosList = await listAll(photosRef);
          await Promise.all(photosList.items.map((item) => deleteObject(item)));
        } catch {
          // Storage folder might not exist — continue
        }
        // Delete profile and users docs
        await deleteDoc(doc(db, "profiles", profile.id));
        await deleteDoc(doc(db, "users", profile.id)).catch(() => {});
        navigate("/admin/users");
        return;
      }

      const nextChanges = { ...(action.changes || {}) };
      if (nextChanges.forceLogoutAt === "SERVER_TIMESTAMP") {
        nextChanges.forceLogoutAt = serverTimestamp();
      }
      if (nextChanges.verifiedAt === "SERVER_TIMESTAMP") {
        nextChanges.verifiedAt = serverTimestamp();
      }
      if (nextChanges.reviewedAt === "SERVER_TIMESTAMP") {
        nextChanges.reviewedAt = serverTimestamp();
      }

      // Handle premium upgrade with plan selection
      if (action.needsPlanSelection && selectedPlan) {
        const now = new Date();
        let expiresAt;

        if (selectedPlan.id === "premium_custom" && selectedPlan.customDate) {
          // Admin set a specific end date — expire at end of that day
          expiresAt = new Date(selectedPlan.customDate + "T23:59:59");
        } else {
          expiresAt = new Date(now);
          expiresAt.setMonth(expiresAt.getMonth() + selectedPlan.months);
        }

        nextChanges.membership = "premium";
        nextChanges.membershipPlanId = selectedPlan.id;
        nextChanges.membershipPlanName = selectedPlan.name;
        nextChanges.membershipDurationMonths = selectedPlan.months || null;
        // Store as Firestore Timestamps so the backend cron job's
        // .where("membershipExpiresAt", "<=", now) comparison works correctly.
        nextChanges.membershipStartedAt = Timestamp.fromDate(now);
        nextChanges.membershipExpiresAt = Timestamp.fromDate(expiresAt);
        nextChanges.membershipUpdatedAt = serverTimestamp();
        nextChanges.membershipSource = "admin"; // marks this as an admin-assigned grant
      }

      await updateDoc(doc(db, "profiles", profile.id), {
        ...nextChanges,
        updatedAt: serverTimestamp(),
      });

      // Sync membership-related changes to users collection too
      const membershipFields = ["membership", "membershipPlanId", "membershipExpiresAt",
        "membershipStartedAt", "membershipUpdatedAt", "membershipSource",
        "membershipDurationMonths", "membershipDowngradedAt"];
      const userChanges = Object.fromEntries(
        Object.entries(nextChanges).filter(([key]) => membershipFields.includes(key))
      );
      if (Object.keys(userChanges).length > 0) {
        const uid = profile.uid || profile.id;
        if (uid) {
          await setDoc(doc(db, "users", uid), {
            ...userChanges,
            updatedAt: serverTimestamp(),
          }, { merge: true });
        }
      }

      setProfile((current) => ({
        ...current,
        ...nextChanges,
        forceLogoutAt:
          action.key === actionCatalog.forceLogout.key ? new Date().toISOString() : current?.forceLogoutAt,
      }));

      setNotice(action.successMessage);
      setPendingAction(null);
    } catch (actionError) {
      setError(actionError.message || "Admin action failed.");
    } finally {
      setSavingKey("");
    }
  };

  const openAction = (action) => {
    if (action.key === actionCatalog.editProfile.key) {
      runAction(action);
      return;
    }
    if (action.key === actionCatalog.copyBiodata.key) {
      copyBiodata();
      return;
    }
    setPendingAction(action);
  };

  const copyBiodata = async () => {
    if (!profile) return;

    const text = buildBiodataText(profile);
    if (!text) {
      setNotice("No biodata available to copy.");
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopiedBiodata(true);
      setNotice("Biodata copied to clipboard.");
      window.setTimeout(() => setCopiedBiodata(false), 2000);
    } catch (copyError) {
      setError(copyError.message || "Unable to copy biodata.");
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
         <button
  type="button"
  onClick={() => navigate(-1)}
  className="inline-flex items-center gap-2 rounded-2xl border border-sand-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-ink-700 shadow-soft transition hover:border-brand-600 hover:text-brand-600"
>
  <ArrowLeft size={16} />
  Back
</button>
          <h1 className="mt-4 font-display text-3xl font-bold text-ink-800">User Management</h1>
          <p className="mt-1 text-[13.5px] text-ink-400">Review profile details and apply admin actions</p>
        </div>

        {profile && (
          <div className="flex items-center gap-3">
            <ActionMenu groups={actionGroups} profile={profile} savingKey={savingKey} onAction={openAction} />
          </div>
        )}
      </div>

      {loading ? (
        <div className="rounded-[28px] border border-[#E7E0DC] bg-white p-8">
          <div className="h-10 w-52 animate-pulse rounded-2xl bg-[#FAF7F2]" />
          <div className="mt-6 grid gap-4 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="h-28 animate-pulse rounded-[24px] bg-[#FAF7F2]" />
            ))}
          </div>
        </div>
      ) : error && !profile ? (
        <div className="rounded-[28px] border border-red-200 bg-red-  50 p-6 text-red-700">{error}</div>
      ) : (
        profile && (
          <div className="space-y-6">
            {(notice || error) && (
              <div
                className={`rounded-[24px] border px-5 py-4 text-sm font-medium ${
                  error ? "border-red-200 bg-red-50 text-red-700" : "border-green-200 bg-green-50 text-green-700"
                }`}
              >
                {error || notice}
              </div>
            )}

            <section className="overflow-hidden rounded-[28px] border border-sand-200 bg-white shadow-soft">
              <div className="border-b border-sand-200 bg-brand-soft px-6 py-6 lg:px-8">
                <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
                  <div className="flex items-center gap-5">
                    <div className="h-28 w-28 overflow-hidden rounded-[24px] border border-[#E7E0DC] bg-[#FAF7F2]">
                      {imageUrls[0] ? (
                        <img src={imageUrls[0]} alt={profile.fullName || "User"} className="h-full w-full object-cover object-center" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-sm font-semibold text-[#c90243]">
                          No Photo
                        </div>
                      )}
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-[#231815]">{profile.fullName || "No Name"}</h2>
                      {profile.kmProfileId && (
                        <span className="mt-1.5 inline-block rounded-full bg-[#fff0f4] border border-[#f5c6d4] px-3 py-0.5 text-[12px] font-bold text-[#c90243]">
                          {profile.kmProfileId}
                        </span>
                      )}
                      <p className="mt-2 text-[#6B5E57]">{profile.mobileNumber || profile.phoneNumber || "-"}</p>
                      <p className="mt-1 text-[#6B5E57]">{profile.presentResidence || profile.cityTown || "-"}</p>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-4">
                    <div className="rounded-[22px] border border-[#EFE4DD] bg-white px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B766B]">Plan</p>
                      <p className="mt-2 text-base font-bold capitalize text-[#231815]">{profile.membership || "free"}</p>
                    </div>
                    <div className="rounded-[22px] border border-[#EFE4DD] bg-white px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B766B]">Status</p>
                      <p className="mt-2 text-base font-bold capitalize text-[#231815]">{profile.status || "active"}</p>
                    </div>
                    <div className="rounded-[22px] border border-[#EFE4DD] bg-white px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B766B]">Verification</p>
                      <p className="mt-2 text-base font-bold text-[#231815]">{profile.profileStatus || "-"}</p>
                    </div>
                    <div className="rounded-[22px] border border-[#EFE4DD] bg-white px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B766B]">Flags</p>
                      <p className="mt-2 text-base font-bold text-[#231815]">
                        {[profile.banned && "Ban", profile.fraudFlag && "Fraud", profile.adminHidden && "Hidden"]
                          .filter(Boolean)
                          .join(", ") || "None"}
                      </p>
                    </div>
                </div>
              </div>
              </div>
<div className="px-5 py-5 lg:px-6">
  {/* Membership plan details */}
  <div className="mb-5">
    <MembershipCard profile={profile} />
  </div>

  {/* Photos at top */}
  {imageUrls.length > 0 && (
    <section className="mb-5 rounded-[24px] border border-[#E7E0DC] bg-white p-5">
      <h3 className="text-lg font-bold text-[#231815]">Photos ({imageUrls.length})</h3>
      <div className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-8">
        {imageUrls.map((url, idx) => (
          <button
            key={url}
            type="button"
            onClick={() => setLightboxIdx(idx)}
            className="group relative overflow-hidden rounded-xl border border-[#EFE4DD] bg-[#FAF7F2] transition hover:shadow-lg hover:border-brand-600"
          >
            <img src={url} alt={`Photo ${idx + 1}`} className="aspect-square w-full object-cover object-center transition group-hover:scale-105" />
            {idx === 0 && (
              <span className="absolute inset-x-0 bottom-0 bg-brand-600/90 py-0.5 text-center text-[7px] font-bold uppercase text-white">
                Default
              </span>
            )}
          </button>
        ))}
      </div>
    </section>
  )}

  <div className="grid gap-5 xl:grid-cols-2">
    {sections.map((section) => (
      <section
        key={section.title}
        className="
          overflow-hidden rounded-[24px]
          border border-[#ECE7E3]
          bg-white
        "
      >
        {/* HEADER */}

        <div
          className="
            border-b border-[#F3EEEA]
            px-5 py-4
          "
        >
         <div className="flex items-center justify-between">
  <h3
    className="
      text-lg font-bold
      text-[#1F1F1F]
    "
  >
    {section.title}
  </h3>

  {section.title === "Account" && (
    <button
      type="button"
      onClick={() =>
        setOpenAccount(
          (value) => !value
        )
      }
      className="
        flex h-9 w-9
        items-center justify-center
        rounded-xl
        bg-[#FAF7F2]
        text-[#c90243]
        transition hover:bg-[#F3ECE8]
      "
    >
      {openAccount ? (
        <X size={16} />
      ) : (
        <Eye size={16} />
      )}
    </button>
  )}
</div>
        </div>

        {/* CONTENT */}

       {section.title !== "Account" ||
openAccount ? (
<div className="px-5 py-2">
          {section.fields.map(
            (field, index) => (
              <div
                key={`${section.title}-${field.key}`}
                className={`
                  flex items-start gap-3
                  py-3
                  ${
                    index !==
                    section.fields.length - 1
                      ? "border-b border-[#F7F3F0]"
                      : ""
                  }
                `}
              >
                {/* LABEL */}

                <div
                  className="
                    min-w-[140px]
                    max-w-[140px]
                  "
                >
                  <p
                    className="
                      text-[13px]
                      font-semibold
                      text-[#c90243]
                    "
                  >
                    {field.label}
                  </p>
                </div>

                {/* VALUE */}

                <div className="flex-1">
                  <p
                    className="
                      break-words
                      text-[14px]
                      leading-6
                      text-[#2B2B2B]
                    "
                  >
                    {formatValue(
                      field.value,
                      field.key
                    )}
                  </p>
                </div>
              </div>
            )
          )}
        </div>
        ) : (
  <div className="px-5 py-5">
    <div
      className="
        rounded-2xl
        border border-dashed
        border-[#E7E0DC]
        bg-[#FCFAF8]
        p-5 text-center
      "
    >
      <p
        className="
          text-sm font-medium
          text-[#777]
        "
      >
        Click eye icon to view
        account details
      </p>
    </div>
  </div>
)}
      </section>
    ))}
  </div>
</div>
            </section>

            <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
              <div className="space-y-6">
                <section className="rounded-[28px] border border-[#E7E0DC] bg-white p-6">
                  <div className="flex items-center gap-3">
                    <ShieldCheck size={18} className="text-[#c90243]" />
                    <h3 className="text-lg font-bold text-[#231815]">Activity</h3>
                  </div>
                  <div className="mt-4 grid gap-3">
                    <DetailRow label="Last Active At" value={formatValue(profile?.lastActiveAt)} />
                    <DetailRow label="Last Active Page" value={profile?.lastActivePage || "-"} />
                    <DetailRow
                      label="Last Navigation"
                      value={
                        profile?.lastNavigationType
                          ? profile.lastNavigationType === "browser-back-forward" || profile.lastNavigationType === "pop"
                            ? "Browser back/forward"
                            : profile.lastNavigationType
                          : "-"
                      }
                    />
                    <DetailRow label="Navigation Page" value={profile?.lastNavigationPage || "-"} />
                  </div>
                </section>
              </div>
            </div>

            {/* ── Interest Requests ───────────────────────────────────── */}
            <InterestRequestsSection
              interests={interests}
              interestsLoading={interestsLoading}
              onViewProfile={(id) => navigate(`/admin/users/${id}`)}
            />
          </div>
        )
      )}

      {/* Photo Lightbox */}
      {lightboxIdx !== null && imageUrls[lightboxIdx] && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
          onClick={() => setLightboxIdx(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxIdx(null)}
            className="absolute right-4 top-4 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
            aria-label="Close"
          >
            <X size={20} />
          </button>

          <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-[12px] font-semibold text-white backdrop-blur">
            {lightboxIdx + 1} / {imageUrls.length}
          </div>

          {imageUrls.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i - 1 + imageUrls.length) % imageUrls.length); }}
                className="absolute left-4 top-1/2 z-10 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
                aria-label="Previous"
              >
                <ArrowLeft size={20} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i + 1) % imageUrls.length); }}
                className="absolute right-4 top-1/2 z-10 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
                aria-label="Next"
              >
                <span className="rotate-180"><ArrowLeft size={20} /></span>
              </button>
            </>
          )}

          <img
            src={imageUrls[lightboxIdx]}
            alt={`Photo ${lightboxIdx + 1}`}
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: "85vh", maxWidth: "90vw", width: "auto", height: "85vh", objectFit: "contain" }}
            className="rounded-2xl shadow-2xl"
          />

          {imageUrls.length > 1 && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute inset-x-0 bottom-4 flex justify-center"
            >
              <div className="flex max-w-full gap-2 overflow-x-auto rounded-2xl bg-white/10 p-2 backdrop-blur">
                {imageUrls.map((u, i) => (
                  <button
                    key={u + i}
                    onClick={() => setLightboxIdx(i)}
                    className={`h-12 w-12 shrink-0 overflow-hidden rounded-xl border-2 transition ${
                      i === lightboxIdx ? "border-white" : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img src={u} alt="" className="h-full w-full object-cover object-center" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <ActionModal
        action={pendingAction}
        busy={pendingAction?.key === savingKey}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        onClose={() => {
          if (!savingKey) setPendingAction(null);
        }}
        onConfirm={() => runAction(pendingAction)}
      />
    </AdminLayout>
  );
};

export default AdminUserDetail;

